import React from "react";

import {ExternalLink} from "../../component/IconLink";
import ImgGallery from "../../component/ImgGallery";
import {SpecificTag} from "../../views/Tag";

export default function PageContent() {
    return (<>
        <section>
            <h2>About the HaDiKo</h2>
            <p>
                The <ExternalLink href="https://hadiko.de">HaDiKo</ExternalLink> is Germany's largest self-administered student dormitory, offering a whopping 1102 rooms.
                But what makes it so special is not its size, but the self-administration part.
                The dormitory has its own politics along with elected politicians, boards and meetings.
                Each of the six buildings acts as a federal state with its own boards and meetings.
                Just looking at the <ExternalLink href="https://www.hadiko.de/leben/selbstverwaltung">self-administration</ExternalLink> organizational chart will give you an idea.
                Even the procedures for moving in and out as well as the room allocation is done by students!
                But why all this? Well, it means the students take more responsibility but gain a lot of freedom.
            </p>
            <p>
                For example, there are &lsquo;working groups&rsquo;, a group of people with a common hobby that gets a room and finances to buy equipment.
                Which working group is founded and whether it gets its requested funds is decided by the politicians.
                There is a long list of active working groups, including:
                a wood workshop, a metal workshop, an electrical laboratory, but also a music room, a training room, a photo laboratory and many more.
                <br />
                There are also many services, that a &lsquo;minister&rsquo; manages and lends out to others.
                For example: a VR room with equipment, a laminator, a billiard room, projectors, a sewing machine and even a sauna.
            </p>
            <p>
                All of these possibilities and offers are something a regular uni- or privately owned dormitory would never have.
                By the way, almost all of this commitment is done voluntarily with no money involved!
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-17-31-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-17-31-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-17-31-32.jpg"),
                    alt : "The stage during the day",
                    title : "The Heimfest in the first few hours."
                }, {
                    src : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-22-33-31-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-22-33-31-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-22-33-31-32.jpg"),
                    alt : "The stage by night",
                    title : "The stage in the last few hours of the Heimfest."
                }
            ]} />
        </section>

        <section>
            <h2>The Heimfest</h2>
            <p>
                The HaDiKo Heimfest is a yearly event where all the self-administration culminates into an anniversary celebration.
                Most of the time it's an outdoor concert right on the dormitory grounds, organized by a team of around 15 people and many more helpers.
                We set up multiple food and beverage stands and this year we had a total of 3 stages: 2 outdoor and 1 indoor with a total of 9 acts.
                The Heimfest is also publicly registered, meaning it must be quite professional to pass hygiene- and safety-regulations.
            </p>
            <p>
                The &lsquo;orga&rsquo; starts its work many months before the Heimfest usually, because there is a lot to consider and discuss.
                Every person in the orga has a dedicated role like finances, renting material, publicity, sponsoring, music and band contact, light technician, sound technician, stand manager for each stand and catering.
                The setup before, and the cleaning up after are supported by many voluntary helpers.
                Same goes for the many shifts that need to be filled at the stands and in the security team.
            </p>
            <p>
                My role this Heimfest was publicity, <SpecificTag name="design" showCount={true} />, <SpecificTag name="print" showCount={true} /> and sponsoring.
                So my goal was to develop a unique brand identity, create promotional material in both print and digital and make sure as many people as possible got wind of the Heimfest.
                Additionally, I wanted to use that same design for all the signage that we put up at the Heimfest itself.
            </p>
        </section>

        <section>
            <h2>Creating the promotional material</h2>
            <p>
                For the design I chose a bright green already associated with the HaDiKo dormitory and added two shades of highly contrasting orange.
                The unique green background is a generated pattern of various shades of green in an irregular grid of triangles.
                The triangles are easy to blend to white, which allows for fun &lsquo;spy holes&rsquo; to display text or photos in.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2022/collage-material.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/collage-material-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/collage-material-32.png"),
                    alt : "Promotional material, tickets and badges",
                    title : "Overview of promotional print material."
                }
            ]} />
            <p>
                This year's Heimfest was special, as it was the 70th anniversary of the umbrella association <ExternalLink href="https://www.studentenwohnheim-ev.de/">Studierendenwohnheim des KIT e.V.</ExternalLink> as well as the 30th anniversary of the <ExternalLink href="https://hadiko.de/verein">HaDiKo e.V.</ExternalLink> itself - making this a 100 year anniversary when applying some creative math.
                That was something I wanted to incorporate into the logo and the design language in itself.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2022/early-design-concepts-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/early-design-concepts-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/early-design-concepts-32.png"),
                    alt : "Early iterative logo design process for the HaDiKo Heimfest 2022",
                    title : "Iterating on the logo was especially fun and - as it often does - simplicity won in the end."
                }
            ]} />
            <p>
                Experimenting with different geometric shapes as a background, I quickly found a triangular pattern of various shades of green to be an eye-catcher.
                Being a programmer, I did not want to create the pattern by hand, so instead I implemented a variation of the <ExternalLink href="https://en.wikipedia.org/wiki/Delaunay_triangulation" type="wiki">Delaunay triangulation</ExternalLink> algorithm to generate a set of background images that I would be able to use for posters, banners, etc.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2022/pattern-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/pattern-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/pattern-32.png"),
                    alt : "Delaunay triangulation",
                    title : "High-resolution triangle pattern generated using the Delaunay triangulation."
                }
            ]} />
        </section>

        <section>
            <h2>T-Shirts</h2>
            <p>
                The orga team would get their own shirt - both as a memento for later, but also to function as somewhat of a uniform during the party and its setup.
                Unfortunately, the green color of the ordered shirts was a little bit off, but the print itself came out great, with striking colors and a memorable design.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2022/shirt-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/shirt-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/shirt-32.png"),
                    alt : "Shirt design for the orga team of the Heimfest 2022",
                    title : "Shirt design with multicolored print of logo at the front and a banderole on the back."
                }
            ]} />
        </section>

        <section>
            <h2>Signposting the Heimfest</h2>
            <p>
                Quite an important task I barely mentioned so far is the signage to be put up during the Heimfest.
                It would show prices, direct the way towards the exit or the toilets and give the guest information in general.
                In total, I created 42 different signs.
            </p>
            <p>
                I very much liked how the signs came out. The green plus the two shades of orange were very easy to use for headlines and subtitles. I especially like the mixed use of both oranges in headlines and the icons.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2022/collage-signs.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/collage-signs-1080.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/collage-signs-32.png"),
                    alt : "Signage for during the Heimfest",
                    title : "Compilation of some of the signs including price lists, rules, information, directions and more."
                }
            ]} />
            <p>
                Collecting the details to put on the signs required strong co-operation with the rest of the orga:
                <br />
                Which objects are illegal to bring onto the grounds? Ask the security team.
                <br />
                Is there deposit for the plates? Ask the food stall planner.
                <br />
                Do the guests pay first and are then controlled or the other way around? Ask entry control.
                <br />
                Actually, during these kinds of early discussions, we would often identify issues my counterpart had not yet thought about.
            </p>

            <ImgGallery images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2022/iconset-1920.png"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/iconset-540.png"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/iconset-32.png"),
                    alt : "Custom icon set for the HaDiKo Heimfest 2022",
                    title : "Needing a lot of unique icons, I created a custom set of icons."
                }
            ]} />

            <p>
                Looking at standard icon sets online, most were missing crucial icons one would need for a party. (Does anyone really <i>need</i> icons for a party?)
                Not having custom icons in one of my personal projects was not really an option anyways ;)
                I created this set of vector-based icons in Adobe Photoshop as one big file and used the slices tool to export the icons as separate files.
            </p>
        </section>

        <section>
            <h2>Gallery</h2>
            <ImgGallery cols="2" images={[
                {
                    src : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-32-16-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-32-16-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-32-16-32.jpg"),
                    alt : "Crowd of party guests in front of stage",
                    title : "With acts both indoor and outdoor, there was something for everyone!"
                }, {
                    src : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-20-30-57-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-20-30-57-1080.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-20-30-57-32.jpg"),
                    alt : "Signage for during the Heimfest",
                    title : "Of course, there are always last-minute adjustments, no matter the planning."
                }, {
                    src : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-29-52-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-29-52-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-21-29-52-32.jpg"),
                    alt : "Outdoor stage",
                    title : "The technicians setup professional sound and light."
                }, {
                    src : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-23-59-27-1920.jpg"),
                    src_thumb : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-23-59-27-540.jpg"),
                    src_thumb_preload : require("../../../media/projects/hadiko-heimfest-2022/2022-07-02-Heimfest-23-59-27-32.jpg"),
                    alt : "The atmosphere in front of the stage was electric",
                    title : "Last dance of the Heimfest before it was closed."
                },
            ]} />

        </section>
    </>);
}
